import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Empty, Flex, Form, Input, QRCode, Space, Spin, Tag,
    Typography, message,
} from 'antd';
import {
    CopyOutlined, PlusOutlined, ReloadOutlined, SaveOutlined,
} from '@ant-design/icons';
import CustomCard from '../../components/Card';
import OneApiService from '../../APIServices/OneApiService';
import {
    BRAND_DEFAULTS, PartnerForm, PhonePreview, toPayload,
} from './PartnerAppEditor';
import { dateTime } from './oneHelpers';

const { Title, Text, Paragraph } = Typography;

/**
 * Partner apps — the white-label editions of YouGuide One.
 *
 * A partner does not get a copy of the app. They get a configuration of it:
 * their name, logo and colours, and only the countries they sell. The
 * configuration id is permanent, so a partner can re-skin the app without
 * invalidating the QR code already printed on their flyers.
 *
 * WHY THREE COLUMNS RATHER THAN A LIST AND A DRAWER. Re-skinning is iterative
 * — a colour, a look, another colour — and a drawer makes every look cost an
 * open and a close. Here the list, the form and the phone are all on screen at
 * once, so the loop is type-and-look. The partner being edited is also always
 * visible on the left, which is what stops one partner's colours being saved
 * under another's name.
 *
 * THE COUNTRY LIST SCOPES EVERY MODULE. A London edition's customers see
 * London eSIMs, London guides and the languages spoken there. That is enforced
 * server-side from the stored configuration, not from anything the app sends.
 */
export default function OnePartnerApps() {
    const [form] = Form.useForm();

    const [configs, setConfigs] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [draftNew, setDraftNew] = useState(false);
    const [countries, setCountries] = useState([]);
    const [filter, setFilter] = useState('');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Watched so the phone tracks typing rather than waiting for a save.
    const values = Form.useWatch([], form) || {};

    const selected = useMemo(
        () => configs.find((c) => c.configurationId === selectedId) || null,
        [configs, selectedId],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const rows = await OneApiService.listConfigurations();
            setConfigs(rows);
            // Select the first only when nothing is selected yet. The
            // functional form keeps `selectedId` out of this callback's
            // dependencies, so a refresh does not yank the operator back to
            // the top of the list.
            setSelectedId((current) => current ?? rows[0]?.configurationId ?? null);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // The destinations we actually sell, so the country field is a list to
    // pick from rather than a box to mistype an ISO code into.
    useEffect(() => {
        OneApiService.previewDestinations()
            .then((d) => setCountries(d.destinations))
            // A failure here costs the dropdown its options, not the page its
            // function: the field still accepts typed codes.
            .catch(() => setCountries([]));
    }, []);

    // Load whichever partner is selected into the form. Keyed on the id and
    // the revision so a save elsewhere refreshes the fields, while typing
    // between saves is never interrupted.
    useEffect(() => {
        if (draftNew) {
            form.setFieldsValue(BRAND_DEFAULTS);
            return;
        }
        if (selected) form.setFieldsValue({ ...BRAND_DEFAULTS, ...selected });
    }, [selected?.configurationId, selected?.revision, draftNew, form, selected]);

    const countryOptions = useMemo(
        () => countries.map((d) => ({
            value: d.iso2,
            label: `${d.flag || ''} ${d.name} (${d.iso2})`.trim(),
        })),
        [countries],
    );

    const shown = useMemo(() => {
        const needle = filter.trim().toLowerCase();
        if (!needle) return configs;
        return configs.filter((c) => (
            c.appName?.toLowerCase().includes(needle)
            || c.configurationId?.toLowerCase().includes(needle)
        ));
    }, [configs, filter]);

    const linkFor = (id) => `https://app.youguide.com/app/${id}`;

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            message.success('Copied');
        } catch {
            message.error('Could not copy — select the text instead');
        }
    };

    const startNew = () => {
        setDraftNew(true);
        setSelectedId(null);
        form.setFieldsValue(BRAND_DEFAULTS);
    };

    const pick = (config) => {
        setDraftNew(false);
        setSelectedId(config.configurationId);
    };

    const save = async () => {
        let v;
        try {
            v = await form.validateFields();
        } catch {
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const body = toPayload(v);
            const saved = selected && !draftNew
                ? await OneApiService.updateConfiguration(selected.configurationId, body)
                : await OneApiService.createConfiguration(body);

            message.success(
                selected && !draftNew
                    ? 'Saved. Installed apps pick this up within seconds — the link is unchanged.'
                    : `Created ${saved.configurationId}.`,
            );
            setDraftNew(false);
            setSelectedId(saved.configurationId);
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const editingSomething = draftNew || Boolean(selected);

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Flex justify="space-between" align="center" wrap gap={12}>
                    <div>
                        <Title level={4} style={{ margin: 0 }}>Partner apps</Title>
                        <Text type="secondary">
                            One app engine, many identities. Each partner gets a permanent link.
                        </Text>
                    </div>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={startNew}>
                            New partner app
                        </Button>
                    </Space>
                </Flex>

                {error && <Alert type="error" showIcon message={error} />}

                <Flex gap={16} align="flex-start" wrap>
                    {/* ---------------------------------------------- column 1 */}
                    <div style={{ width: 268, flex: '0 0 268px' }}>
                        <CustomCard title="Partners">
                            <Input.Search
                                placeholder="Search partners"
                                allowClear
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                style={{ marginBottom: 12 }}
                            />

                            {configs.length === 0 && !loading && (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No partner apps yet"
                                >
                                    <Button type="primary" size="small" onClick={startNew}>
                                        Create the first one
                                    </Button>
                                </Empty>
                            )}

                            {draftNew && (
                                <PartnerRow
                                    name={values.appName?.trim() || 'New partner app'}
                                    meta="Unsaved"
                                    status="draft"
                                    active
                                    onClick={() => {}}
                                />
                            )}

                            {shown.map((c) => (
                                <PartnerRow
                                    key={c.configurationId}
                                    name={c.appName}
                                    meta={`${c.allowedCountries?.length
                                        ? `${c.allowedCountries.length} countries`
                                        : 'Everywhere'} · r${c.revision}`}
                                    status={c.status}
                                    active={!draftNew && c.configurationId === selectedId}
                                    onClick={() => pick(c)}
                                />
                            ))}

                            {configs.length > 0 && shown.length === 0 && (
                                <Text type="secondary">No partner matched that.</Text>
                            )}
                        </CustomCard>
                    </div>

                    {/* ---------------------------------------------- column 2 */}
                    <div style={{ flex: 1, minWidth: 340 }}>
                        <CustomCard
                            title={draftNew
                                ? 'New partner app'
                                : (selected ? `Edit ${selected.appName}` : 'Branding')}
                            extra={editingSomething && (
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    loading={saving}
                                    onClick={save}
                                >
                                    {draftNew ? 'Create' : 'Save changes'}
                                </Button>
                            )}
                        >
                            {!editingSomething ? (
                                <Text type="secondary">
                                    Pick a partner on the left, or create a new one.
                                </Text>
                            ) : (
                                <>
                                    {!draftNew && selected && (
                                        <Alert
                                            type="info"
                                            showIcon
                                            style={{ marginBottom: 16 }}
                                            message="The link and the QR code never change"
                                            description={
                                                `Customers keep using ${selected.configurationId}. `
                                                + 'Only the appearance moves, so anything already '
                                                + 'printed stays valid.'
                                            }
                                        />
                                    )}
                                    <PartnerForm
                                        form={form}
                                        disabled={saving}
                                        countryOptions={countryOptions}
                                    />
                                </>
                            )}
                        </CustomCard>
                    </div>

                    {/* ---------------------------------------------- column 3 */}
                    <div style={{ flex: '0 0 300px', width: 300 }}>
                        <CustomCard title="How it will look">
                            {editingSomething
                                ? <PhonePreview values={values} />
                                : <Text type="secondary">Nothing selected.</Text>}
                        </CustomCard>

                        {!draftNew && selected && (
                            <CustomCard title="How to share it" style={{ marginTop: 16 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <QRCode
                                        value={linkFor(selected.configurationId)}
                                        size={150}
                                        bordered={false}
                                    />
                                    <Paragraph
                                        type="secondary"
                                        style={{ marginTop: 8, fontSize: 12 }}
                                    >
                                        Printable. Scanning it opens the app
                                        as {selected.appName}.
                                    </Paragraph>
                                </div>

                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <CopyRow
                                        label="Configuration ID"
                                        value={selected.configurationId}
                                        onCopy={copy}
                                    />
                                    <CopyRow
                                        label="App link"
                                        value={linkFor(selected.configurationId)}
                                        onCopy={copy}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Updated {dateTime(selected.updatedAt)}
                                    </Text>
                                </Space>
                            </CustomCard>
                        )}
                    </div>
                </Flex>
            </Space>
        </Spin>
    );
}

function PartnerRow({ name, meta, status, active, onClick }) {
    const tone = { active: 'green', paused: 'orange', draft: 'default' }[status];

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
            style={{
                padding: '9px 10px',
                marginBottom: 6,
                borderRadius: 8,
                cursor: 'pointer',
                background: active ? 'rgba(1,24,72,.06)' : 'transparent',
                borderLeft: `3px solid ${active ? '#001848' : 'transparent'}`,
            }}
        >
            <Flex justify="space-between" align="center" gap={8}>
                <Text
                    strong={active}
                    ellipsis
                    style={{ minWidth: 0 }}
                >
                    {name}
                </Text>
                <Tag color={tone} style={{ marginInlineEnd: 0 }}>{status}</Tag>
            </Flex>
            <Text type="secondary" style={{ fontSize: 11 }}>{meta}</Text>
        </div>
    );
}

function CopyRow({ label, value, onCopy }) {
    return (
        <div>
            <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
            <Flex gap={8} align="center">
                <Text code ellipsis style={{ minWidth: 0, flex: 1 }}>{value}</Text>
                <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => onCopy(value)}
                />
            </Flex>
        </div>
    );
}
